import { useEffect, useMemo, useState } from "react";
import {
  HeartPulse,
  Activity,
  Moon,
  Footprints,
  Flame,
  RefreshCw,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/hooks/use-session";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { EnergyBalanceChart } from "@/components/health/EnergyBalanceChart";
import { useDailyVitals } from "@/hooks/use-daily-vitals";
import type { VitalsRow } from "@/hooks/use-daily-vitals";
import { useTrendDeltas } from "@/hooks/use-trend-deltas";
import { formatDelta } from "@/lib/recovery/trend";
import { Drawer, EmptyState, SkeletonLoader } from "@/components/system";
import { formatShortDate } from "@/lib/date-format";
import { Link } from "react-router-dom";

/**
 * Today vitals strip (BW7). Reads last 30 days of `daily_vitals` +
 * `sleep_record`, picks the most recent row, renders five inline metrics
 * with a 7-day sparkline below each. Tapping a metric opens a 30-day chart
 * in a drawer.
 *
 * Empty-state hint nudges the user to /integrations when no data exists yet.
 */

type MetricId = "hrv" | "rhr" | "sleep" | "steps" | "active";

type Metric = {
  id: MetricId;
  label: string;
  Icon: typeof HeartPulse;
  unit: string;
  read: (row: VitalsRow) => number | null;
  format: (v: number | null) => string;
};

const METRICS: Metric[] = [
  {
    id: "hrv",
    label: "HRV",
    Icon: Activity,
    unit: "ms",
    read: (r) => r.hrv_status_ms,
    format: (v) => (v == null ? "—" : v.toFixed(0)),
  },
  {
    id: "rhr",
    label: "RHR",
    Icon: HeartPulse,
    unit: "bpm",
    read: (r) => r.rhr_bpm,
    format: (v) => (v == null ? "—" : v.toFixed(0)),
  },
  {
    id: "sleep",
    label: "Sleep",
    Icon: Moon,
    unit: "h",
    // HOTFIX-BW18.1 #7: chart Y-axis renders this number directly, so we
    // store hours (not minutes). Card text still formats as H:MM.
    read: (r) => (r.sleep_min != null ? r.sleep_min / 60 : null),
    format: (v) => {
      if (v == null) return "—";
      const h = Math.floor(v);
      const m = Math.round((v - h) * 60);
      return `${h}:${String(m).padStart(2, "0")}`;
    },
  },
  {
    id: "steps",
    label: "Steps",
    Icon: Footprints,
    unit: "",
    read: (r) => r.steps,
    format: (v) =>
      v == null
        ? "—"
        : v >= 10_000
          ? `${(v / 1000).toFixed(1)}k`
          : v.toFixed(0),
  },
  {
    id: "active",
    label: "Active",
    Icon: Flame,
    unit: "kcal",
    read: (r) => r.calories_active,
    format: (v) => (v == null ? "—" : v.toFixed(0)),
  },
];

export function TodayVitals() {
  const q = useDailyVitals(30);
  const deltas = useTrendDeltas();
  const qc = useQueryClient();
  const { session } = useSession();
  const userId = session?.user?.id;
  const [openDetail, setOpenDetail] = useState<Metric | null>(null);

  // Tick every 30s so the "Updated Xs ago" label stays current without
  // calling Date.now() during render (React purity rules).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);

  const rows = q.data ?? [];

  function handleRefresh() {
    void qc.invalidateQueries({ queryKey: ["daily_vitals", userId] });
  }

  if (q.isLoading) {
    return <SkeletonLoader variant="card" height={140} />;
  }

  if (rows.length === 0) {
    return (
      <article
        style={{
          background: "var(--bg-2)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          padding: "var(--space-3)",
        }}
      >
        <EmptyState
          headline="No vitals yet."
          sub="Connect Garmin (via Health Auto Export) or another sensor to start the daily roll-up."
          cta={
            <Link
              to="/integrations"
              style={{
                fontSize: "var(--type-body-s)",
                color: "var(--accent)",
              }}
            >
              Open integrations →
            </Link>
          }
        />
      </article>
    );
  }

  const latest = rows[0];
  const sevenDay = rows.slice(0, 7);

  // Per-metric "most recent available" — different sensors push at
  // different cadences. HRV may be from last night; Steps from a few hours
  // ago. Picking strict latest day causes "—" everywhere when one metric
  // hasn't arrived yet but others have. Walk back until we find a value.
  function lastAvailable(metric: Metric): { value: number | null; row: VitalsRow | null } {
    for (const r of rows) {
      const v = metric.read(r);
      if (v != null) return { value: v, row: r };
    }
    return { value: null, row: null };
  }

  // BW17 Issue 4: header label should track the data, not the refetch.
  // Compute the latest available date across every metric so "Updated X ago"
  // reflects when fresh sensor data actually landed in the rollup.
  const perMetricLatest = METRICS.map((m) => lastAvailable(m).row?.date ?? null);
  const latestObservedDate = perMetricLatest
    .filter((d): d is string => d != null)
    .sort()
    .at(-1) ?? null;
  const latestObservedAgoMs = latestObservedDate
    ? now - new Date(latestObservedDate + "T00:00:00").getTime()
    : null;

  return (
    <article
      aria-label="Today's vitals"
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-4)",
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "var(--space-3)",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "var(--type-body)",
            fontWeight: 500,
            color: "var(--fg)",
          }}
        >
          Vitals
        </h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-3)",
          }}
        >
          <span
            className="mono"
            style={{
              fontSize: "var(--type-meta)",
              color: "var(--fg-3)",
            }}
            title={
              latestObservedDate
                ? `Latest observation: ${latestObservedDate}`
                : undefined
            }
          >
            {latestObservedAgoMs != null
              ? `Updated ${formatTimeAgo(latestObservedAgoMs)}`
              : formatShortDate(latest.date)}
          </span>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={q.isFetching}
            aria-label="Refresh vitals"
            style={{
              width: 28,
              height: 28,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "transparent",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-card)",
              color: "var(--fg-3)",
              cursor: q.isFetching ? "progress" : "pointer",
            }}
          >
            <RefreshCw
              size={14}
              strokeWidth={1.5}
              style={{
                animation: q.isFetching
                  ? "haus-spin 0.8s linear infinite"
                  : undefined,
              }}
            />
          </button>
        </div>
      </header>
      <style>{`
        @keyframes haus-spin {
          from { transform: rotate(0deg) }
          to { transform: rotate(360deg) }
        }
      `}</style>

      <div
        className="haus-vitals-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: "var(--space-2)",
        }}
      >
        <style>{`
          @media (max-width: 480px) {
            .haus-vitals-grid {
              grid-template-columns: repeat(5, 1fr) !important;
              gap: 6px !important;
            }
          }
        `}</style>
        {METRICS.map((m) => {
          const last = lastAvailable(m);
          const deltaText = deltaForMetric(m.id, deltas.data);
          return (
            <MetricCell
              key={m.id}
              metric={m}
              latest={last.value}
              latestDate={last.row?.date ?? null}
              today={latest.date}
              spark={sevenDay.map((r) => m.read(r))}
              deltaText={deltaText}
              onOpen={() => setOpenDetail(m)}
            />
          );
        })}
      </div>

      <Drawer
        open={openDetail != null}
        onClose={() => setOpenDetail(null)}
        title={openDetail ? `${openDetail.label} · 30 days` : ""}
      >
        {openDetail?.id === "active" ? (
          <EnergyBalanceChart days={30} />
        ) : openDetail ? (
          <MetricDetail metric={openDetail} rows={rows} />
        ) : null}
      </Drawer>
    </article>
  );
}

function deltaForMetric(
  id: MetricId,
  deltas: ReturnType<typeof useTrendDeltas>["data"],
): string | null {
  if (!deltas) return null;
  switch (id) {
    case "hrv":
      return formatDelta(deltas.hrv_ms.delta_7d, "ms");
    case "rhr":
      return formatDelta(deltas.rhr_bpm.delta_7d, "bpm");
    case "sleep":
      return formatDelta(deltas.sleep_h.delta_7d, "h");
    case "steps":
      return formatDelta(deltas.steps.delta_7d, "");
    case "active":
      return formatDelta(deltas.active_kcal.delta_7d, "kcal");
  }
}

function MetricCell({
  metric,
  latest,
  latestDate,
  today,
  spark,
  deltaText,
  onOpen,
}: {
  metric: Metric;
  latest: number | null;
  latestDate: string | null;
  today: string;
  spark: (number | null)[];
  deltaText: string | null;
  onOpen: () => void;
}) {
  const Icon = metric.Icon;
  const valueText = metric.format(latest);
  // BW17 Issue 4: every card carries a "<X> ago" subtitle so the user can
  // see at a glance which sensor lagged behind. SLEEP already had this; now
  // HRV / RHR / STEPS / ACTIVE do too. Today's data renders as "today" — we
  // never claim "just now" because daily_vitals lives at day granularity.
  let staleLabel: string | null = null;
  if (latest != null && latestDate != null) {
    if (latestDate === today) staleLabel = "today";
    else staleLabel = formatStale(latestDate, today);
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open ${metric.label} chart`}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        textAlign: "left",
        padding: "var(--space-2) var(--space-2)",
        background: "transparent",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        color: "var(--fg)",
        gap: 4,
        cursor: "pointer",
        minHeight: 88,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          color: "var(--fg-3)",
          fontSize: 10.5,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        <Icon size={12} strokeWidth={1.6} aria-hidden />
        {metric.label}
      </div>
      <div
        className="mono"
        style={{
          fontSize: 18,
          fontWeight: 500,
          letterSpacing: "-0.01em",
          color: latest == null ? "var(--fg-3)" : "var(--fg)",
        }}
      >
        {valueText}
        {metric.unit && (
          <span
            style={{ fontSize: 10, color: "var(--fg-3)", marginLeft: 3 }}
          >
            {metric.unit}
          </span>
        )}
      </div>
      <Spark values={spark} />
      {deltaText && (
        <div
          style={{
            marginTop: 2,
            fontSize: 9.5,
            color: "var(--fg-3)",
            letterSpacing: "0.02em",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {deltaText}
        </div>
      )}
      {staleLabel && (
        <div
          style={{
            marginTop: 2,
            fontSize: 9,
            color: "var(--fg-3)",
            letterSpacing: "0.04em",
          }}
        >
          {staleLabel}
        </div>
      )}
    </button>
  );
}

function formatTimeAgo(deltaMs: number): string {
  const sec = Math.max(0, Math.round(deltaMs / 1000));
  if (sec < 30) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

function formatStale(latestDate: string, today: string): string {
  const a = new Date(latestDate + "T00:00:00Z").getTime();
  const b = new Date(today + "T00:00:00Z").getTime();
  const days = Math.round((b - a) / 86_400_000);
  if (days <= 0) return "";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days / 7)}w ago`;
  return `${Math.round(days / 30)}mo ago`;
}

function Spark({ values }: { values: (number | null)[] }) {
  const data = useMemo(
    () =>
      values
        .slice()
        .reverse()
        .map((v, i) => ({ i, v: v ?? null })),
    [values],
  );
  const real = data.filter((d) => d.v != null);
  if (real.length === 0) {
    return (
      <div
        aria-hidden
        style={{
          height: 18,
          borderTop: "1px dashed var(--hair-2)",
          marginTop: 4,
        }}
      />
    );
  }
  return (
    <div style={{ height: 22, width: "100%" }}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Line
            type="monotone"
            dataKey="v"
            stroke="var(--accent)"
            strokeWidth={1.4}
            dot={false}
            isAnimationActive={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

type MetricTarget = { value: number; label: string };

function metricTarget(id: MetricId): MetricTarget | null {
  switch (id) {
    case "sleep":
      return { value: 8, label: "8h target" };
    case "steps":
      return { value: 10000, label: "10k target" };
    default:
      return null;
  }
}

function MetricDetail({
  metric,
  rows,
}: {
  metric: Metric;
  rows: VitalsRow[];
}) {
  const data = useMemo(
    () =>
      rows
        .slice()
        .reverse()
        .map((r) => ({
          date: r.date,
          v: metric.read(r),
        })),
    [rows, metric],
  );
  const realCount = data.filter((d) => d.v != null).length;
  const target = metricTarget(metric.id);
  // HOTFIX-BW18.1 #7: auto-scale yMax so a 12h sleep displays without
  // clipping AND the target reference line stays visible. Floor at the
  // target + 1 (or +20% for non-target metrics) so a sparse single-point
  // dataset doesn't render as a flat line at the top of the chart.
  const observedMax = data.reduce(
    (m, d) => (d.v != null && d.v > m ? d.v : m),
    0,
  );
  const rawYMax = target
    ? Math.max(target.value + 1, Math.ceil(observedMax * 1.1))
    : observedMax > 0
      ? Math.ceil(observedMax * 1.2)
      : 1;
  // HOTFIX-BW19.4 PR-A Bug 4b: round Steps y-axis to the next 5k so
  // tick labels are stable across days (e.g. 0 / 5k / 10k / 15k) and
  // the 10k target line lands cleanly. Other metrics keep the precise
  // auto-scaled max.
  const yMax =
    metric.id === "steps" ? Math.max(5000, Math.ceil(rawYMax / 5000) * 5000) : rawYMax;
  // Range-aware tick density: every day for ≤14d, every third day for
  // 30d, every seventh for 90d. Recharts renders an XAxis tick when the
  // index modulo `interval + 1` is zero.
  const len = data.length;
  const xAxisInterval = len <= 14 ? 0 : len <= 31 ? 2 : 6;
  // Right-side padding when a target reference line label is shown so the
  // dashed "10k target" / "8h target" caption isn't clipped.
  const rightMargin = target ? 24 : 12;
  const showValueLabels = metric.id === "steps" && len <= 7;

  if (realCount === 0) {
    return (
      <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
        No {metric.label.toLowerCase()} data in the last 30 days.
      </p>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <p
        style={{
          margin: 0,
          fontSize: "var(--type-body-s)",
          color: "var(--fg-2)",
        }}
      >
        Last {realCount} day{realCount === 1 ? "" : "s"} ·{" "}
        <span className="mono">
          {metric.format(rows[0] ? metric.read(rows[0]) : null)}
        </span>{" "}
        latest
      </p>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: rightMargin, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="date"
              stroke="var(--fg-4)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "var(--hair)" }}
              interval={xAxisInterval}
              minTickGap={20}
              tickFormatter={(v: string) => formatShortDate(v)}
            />
            <YAxis
              stroke="var(--fg-4)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "var(--hair)" }}
              width={42}
              domain={[0, yMax]}
              tickFormatter={(v: number) =>
                metric.id === "steps" && v >= 1000 ? `${v / 1000}k` : String(v)
              }
            />
            {target && (
              <ReferenceLine
                y={target.value}
                stroke="var(--accent)"
                strokeDasharray="4 4"
                label={{
                  value: target.label,
                  position: "right",
                  fill: "var(--accent)",
                  fontSize: 10,
                }}
              />
            )}
            <Tooltip
              contentStyle={{
                background: "var(--bg-3)",
                border: "1px solid var(--hair-2)",
                borderRadius: "var(--radius-card)",
                color: "var(--fg)",
                fontSize: "var(--type-body-s)",
              }}
              labelFormatter={(label) => formatShortDate(String(label))}
              formatter={(v) => {
                const n =
                  typeof v === "number"
                    ? v
                    : typeof v === "string"
                      ? Number(v)
                      : null;
                return [
                  metric.format(n == null || Number.isNaN(n) ? null : n),
                  metric.label,
                ] as [string, string];
              }}
              cursor={{ stroke: "var(--hair-2)" }}
            />
            <Line
              type="monotone"
              dataKey="v"
              stroke="var(--accent)"
              strokeWidth={1.5}
              dot={{ r: 2.5, fill: "var(--accent)" }}
              activeDot={{ r: 4 }}
              animationDuration={300}
              connectNulls
              label={
                showValueLabels
                  ? {
                      position: "top",
                      fill: "var(--fg-2)",
                      fontSize: 10,
                      offset: 8,
                      formatter: (v: number) =>
                        v == null
                          ? ""
                          : v >= 1000
                            ? `${(v / 1000).toFixed(1)}k`
                            : v.toFixed(0),
                    }
                  : undefined
              }
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
